import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import { QueueFilterBar, emptyQueueFilters, toQueryFilters } from "@/components/QueueFilters";
import { formatDateTime, displayTimezoneLabel } from "@/shared/datetime";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { PatientSummaryByUhid } from "@/components/PatientSummary";
import { PageHeader } from "@/components/PageHeader";
import { GenericInfoButton } from "@/components/GenericInfoButton";
import { StatusPill } from "@/components/StatusPill";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  medicationOrderQueueApi, type PendingMedicationOrderRow,
  type PendingMedicationOrderStatus, type MedicationOrderBillingPrefill,
  type HealNTrackQueueFilters,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

// Partial and Billed are offered so the counter can find a part-dispensed prescription to finish,
// and confirm one that is already done, without leaving the queue.
const STATUSES: ("" | PendingMedicationOrderStatus)[] = ["", "Ready", "InBilling", "Partial", "Billed"];

/**
 * HealNTrack prescription queue — medication orders pushed from HealNTrack, waiting to be
 * billed at the OP counter. "Bill" claims the order (soft-locks it so two counters can't
 * double-bill), resolves the customer, and hands the prefill to the OP billing form.
 */
export function PrescriptionQueuePage() {
  const navigate = useNavigate();
  const canBill = useAuth(s => s.hasPermission("sales.invoice.create"));

  const [status, setStatus] = useState<"" | PendingMedicationOrderStatus>("Ready");
  const [filters, setFilters] = useState<HealNTrackQueueFilters>(emptyQueueFilters);
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [prefill, setPrefill] = useState<MedicationOrderBillingPrefill | null>(null);

  const applied = toQueryFilters(filters);
  const query = useQuery({
    queryKey: ["rx-queue", status, applied, pagination.page, pagination.pageSize],
    queryFn: () => medicationOrderQueueApi.queue({
      status: status || undefined, ...applied,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
    refetchInterval: 15000, // near-real-time: new prescriptions surface within ~15s
  });

  const claimMut = useMutation({
    mutationFn: (id: string) => medicationOrderQueueApi.claim(id),
    onSuccess: (data) => setPrefill(data),
  });

  const openInBilling = () => {
    if (!prefill) return;
    // Hand off to the OP billing form; it reads location.state to prefill customer + lines.
    navigate("/sales/invoices/new", { state: { medicationOrder: prefill } });
  };

  const columns: GridColDef<PendingMedicationOrderRow>[] = [
    { field: "orderedOn", headerName: "Ordered", width: 170,
      valueFormatter: (v) => formatDateTime(v as string) },
    { field: "medicationOrderId", headerName: "Order #", width: 100 },
    { field: "uhid", headerName: "Patient", width: 210, sortable: false,
      renderCell: (p) => (
        <Stack spacing={0} sx={{ lineHeight: 1.3, py: 0.5 }}>
          <span>{p.row.patientName || p.row.uhid}</span>
          <Typography variant="caption" color="text.secondary">
            {p.row.uhid}{p.row.patientPhone ? ` · ${p.row.patientPhone}` : ""}
          </Typography>
        </Stack>
      ) },
    // Falls back to the id: orders received before HealNTrack started sending the name have none.
    { field: "orderByName", headerName: "Doctor", width: 180, sortable: false,
      renderCell: (p) => p.row.orderByName
        ? <span>{p.row.orderByName}</span>
        : <Box component="span" sx={{ color: "text.secondary" }}>#{p.row.orderBy}</Box> },
    { field: "revision", headerName: "Rev", width: 70, type: "number" },
    // A span, not a Typography: the grid centres plain cell text with a row-tall line-height,
    // and a block element opts out of that line box — the count then sat high in the row,
    // out of line with every other column.
    { field: "lineCount", headerName: "Dispensed", width: 110, sortable: false,
      renderCell: (p) => {
        const done = p.row.dispensedLineCount, total = p.row.lineCount;
        return (
          <Box component="span"
            sx={{ color: done === 0 ? "text.secondary" : done < total ? "warning.main" : "success.main" }}>
            {done} / {total}
          </Box>
        );
      } },
    { field: "unmappedCount", headerName: "Unmapped", width: 110, type: "number",
      renderCell: (p) => (p.value as number) > 0
        ? <Chip size="small" color="warning" label={p.value as number} />
        : <span>0</span> },
    { field: "status", headerName: "Status", width: 130,
      renderCell: (p) => <StatusPill label={p.value as string} /> },
    { field: "actions", headerName: "", width: 130, sortable: false, filterable: false,
      renderCell: (p) => {
        if (!canBill) return null;
        // Hard-block: an order with an unmapped line can't be completed (the backend rejects
        // /complete with 422 UNMAPPED_LINE), so don't let billing start. Wait for the item-sync
        // job or map the product in the catalog first.
        const blocked = p.row.unmappedCount > 0;
        return (
          <Tooltip title={blocked
            ? `${p.row.unmappedCount} item(s) not mapped to the catalog — wait for item sync or map them before billing`
            : ""}>
            <span>
              <Button size="small" startIcon={<ReceiptLongIcon />}
                disabled={blocked || claimMut.isPending}
                onClick={() => claimMut.mutate(p.row.id)}>
                Bill
              </Button>
            </span>
          </Tooltip>
        );
      } },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Sales", "Prescription queue"]}
        title="Prescription queue"
        // The zone is named so nobody has to wonder whose clock the "Ordered" column is on.
        subtitle={`Medication orders from HealNTrack, waiting to be billed at the OP counter. `
          + `Times shown in ${displayTimezoneLabel()}.`}
      />

      <Paper sx={{ p: 2 }}>
        <QueueFilterBar
          value={filters} onChange={setFilters}
          leading={
            <TextField select label="Status" value={status} size="small"
              onChange={(e) => setStatus(e.target.value as "" | PendingMedicationOrderStatus)} sx={{ minWidth: 160 }}>
              {STATUSES.map(s => <MenuItem key={s} value={s}>{s || "All active"}</MenuItem>)}
            </TextField>
          }
        />
        {(query.isError || claimMut.isError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage(query.error ?? claimMut.error)}
          </Alert>
        )}
        <Box sx={{ height: 560 }}>
          <DataGrid
            rows={query.data?.items ?? []} columns={columns} loading={query.isLoading}
            paginationMode="server" rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination} onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]} disableRowSelectionOnClick
            getRowId={(row) => row.id}
          />
        </Box>
      </Paper>

      <Dialog open={prefill !== null} onClose={() => setPrefill(null)} maxWidth="sm" fullWidth>
        <ClosableDialogTitle onClose={() => setPrefill(null)}>
          Prescription {prefill?.medicationOrderId} · rev {prefill?.revision}
        </ClosableDialogTitle>
        <DialogContent>
          {prefill && (
            <Box sx={{ mb: 2 }}>
              <PatientSummaryByUhid dense uhid={prefill.uhid} fallbackName={prefill.customerName} />
            </Box>
          )}
          {prefill?.lines.some(l => !l.isMapped) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This prescription has item(s) not yet mapped to the catalog, so it can't be billed yet —
              billing would be blocked at completion. Wait for the item-sync job or map the product in
              the catalog, then bill.
            </Alert>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell>Mapped</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prefill?.lines.map((l) => (
                <TableRow key={l.medicationOrderTId}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>{l.brandName ?? `#${l.medicationId}`}</span>
                      <GenericInfoButton genericNameId={l.genericNameId} genericName={l.genericName} />
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{l.qty}</TableCell>
                  <TableCell>
                    <Chip size="small" color={l.isMapped ? "success" : "warning"}
                      label={l.isMapped ? "Yes" : "No"} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrefill(null)}>Cancel</Button>
          <Button variant="contained" onClick={openInBilling}
            disabled={prefill?.lines.some(l => !l.isMapped) ?? false}>
            Open in OP billing
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
